import { useEffect, useState } from 'react';

import { Link, useFetcher, useParams } from 'react-router';

import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';

import { info as boredGameCompany } from '@boc/organization/brands/bgc';
import { info as bandOfCapes } from '@boc/organization/brands/boc';

import { channels } from '#/channels';

import toast from '~/toast';
import type { TFMLinks } from '~/types';
import { urls } from '~/urls';

import { type action as newsletterAction } from '~/framework/newsletter/route';
import { schemaForm } from '~/framework/newsletter/schema';

import { JigsawIcon } from '~/local/components/icons';
import generate from '~/local/menus/footer';

import { brandInfo } from '#/brand';

export default function Footer({
  tfmLinks,
  missingPiecesAvailable,
}: {
  tfmLinks: TFMLinks[] | null;
  missingPiecesAvailable?: boolean;
}) {
  const params = useParams();
  const entity = channels[params.channel as keyof typeof channels]?.entity;
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const fetcher = useFetcher<typeof newsletterAction>();

  const footerMenu = generate(params, { missingPieces: !!missingPiecesAvailable });

  const lastResult = fetcher.data;

  const [form, fields] = useForm({
    id: 'newsletter',
    lastResult,
    defaultValue: {
      email: '',
    },
    shouldValidate: 'onInput',
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: schemaForm });
    },
    onSubmit(event, { formData, submission, action, encType, method }) {
      event.preventDefault();

      return fetcher.submit(event.currentTarget);
    },
  });

  useEffect(() => {
    if (!fetcher.data) return;

    if (fetcher.data && fetcher.data.status == 'success' && fetcher.data.initialValue?.email) {
      if (newsletterEmail !== fetcher.data.initialValue['email']) {
        const subscribedEmail =
          typeof fetcher.data?.initialValue?.email == 'string' && fetcher.data?.initialValue?.email;

        if (subscribedEmail) setNewsletterEmail(subscribedEmail);
        toast.success(`Thanks for subscribing!`, {
          id: `newsletter-success-${subscribedEmail}`,
        });
      }
    }

    if (fetcher.data.status == 'error') {
      toast.error(`An error occured, please try again!`, {
        id: 'newsletter-error',
      });
    }

    return;
  }, [newsletterEmail, fetcher.data]);

  return (
    <div className="bg-primary-1 text-neutral-1">
      {/* {tfmLinks ? (
        <div className="container mx-auto w-full max-w-screen-xl px-6 py-10">
          <div className="grid grid-cols-1 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4">
            {tfmLinks.map((section) => (
              <div key={section.title}>
                <div className="font-heading-condensed text-lg font-medium leading-6 tracking-tight">
                  {section.href ? (
                    <Link className="" to={section.href}>
                      {section.title}
                    </Link>
                  ) : (
                    section.title
                  )}
                </div>
                <ul className="mt-4 flex flex-col gap-y-1 leading-5 tracking-tight">
                  {section.children?.map((link) => (
                    <li key={link.title}>
                      <Link to={link.href}>{link.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ) : null} */}

      <footer className="border-t-primary-1-120 border-t">
        <div className="divide-primary-1-120 container flex w-full max-w-screen-xl flex-col divide-y px-6 xl:mx-auto">
          <div className="grid grid-cols-1 gap-20 py-12 md:grid-cols-2 lg:grid-cols-3">
            <div className="">
              <h3 className="font-heading-condensed text-xl leading-7 font-medium tracking-tight">
                Join the Jigsaw Jamboree
              </h3>
              <div className="mt-4 leading-5 tracking-tight">
                Unlock exclusive deals, sneak peeks, and puzzlingly good fun! Subscribe to our
                newsletter and stay in the puzzle loop.
              </div>
              {/* <fetcher.Form action={urls.newsletter(params)} method="POST" {...getFormProps(form)}>
                <div className="relative mt-2">
                  <input
                    className="peer block w-full border-0 bg-primary-1 py-1.5 text-neutral-1 placeholder:text-neutral-1 sm:text-sm sm:leading-6"
                    placeholder="Enter Email"
                    {...getInputProps(fields.email, { type: 'email' })}
                    onChange={(e) => e.currentTarget.form?.requestSubmit()}
                  />
                  <div
                    className="absolute inset-x-0 bottom-0 border-t border-neutral-1"
                    aria-hidden="true"
                  />
                  <div className="absolute inset-y-0 right-0 flex py-3 pr-1.5 text-neutral-1">
                    <JigsawIcon />
                  </div>
                </div>
                {fields.email.errors && <div className="mt-2 text-sm">{fields.email.errors}</div>}
              </fetcher.Form> */}
            </div>
            <div className="grid grid-cols-1 gap-6 sm:col-span-2 sm:grid-cols-2">
              {footerMenu.map((section) => (
                <div key={section.heading} className="">
                  <div className="font-heading-condensed text-xl leading-7 font-medium tracking-tight">
                    {section.heading}
                  </div>
                  <ul className="mt-4 flex flex-col gap-y-1 leading-5 tracking-tight">
                    {section.links.map((link) => (
                      <li key={link.title}>
                        <Link to={link.to}>{link.title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col justify-between gap-10 py-12 lg:flex-row lg:items-center">
            <div className="flex flex-col">
              <Link className="flex flex-col" to={urls.home(params)}>
                <img
                  className="h-12 w-48 object-contain object-left"
                  src={brandInfo.logo!.svg}
                  alt={[brandInfo.name, brandInfo.tagline].join(' | ')}
                />
              </Link>
              <div className="mt-5 flex flex-col space-y-6">
                <a
                  className="flex flex-col space-y-3"
                  href={`${boredGameCompany.url}?utm_source=jsn&utm_content=footer`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <p className="text-base">Board games at</p>
                  <div className="h-8">
                    <img
                      className=""
                      src={boredGameCompany.logo!.svg}
                      alt={boredGameCompany.name}
                    />
                  </div>
                </a>
                <div className="flex flex-col space-y-3">
                  <p className="text-base">Operated by</p>
                  <div className="h-8">
                    {entity?.logo && (
                      <img className="h-full w-auto max-w-[160px] object-contain" src={entity.logo} alt={entity.name} />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <p className="font-heading-condensed text-l place-self-end leading-7 font-bold tracking-tight">
              Copyright © {new Date().getFullYear()} {entity?.legalEntity}. All rights
              reserved.
            </p>
            <div className="flex flex-col items-end gap-4 place-self-end">
              <img src="/static/payments.png" alt="Accepted payment methods" width={260} />
              <p className="font-heading-condensed text-xl leading-7 font-bold tracking-tight">
                {brandInfo.funTagline}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
